import React, { useState } from "react";
import {
  Box,

  Flex,
  VStack,
  HStack,
  Heading,
  Text,
  Grid,
  GridItem,

  Icon,
  Separator,
} from "@chakra-ui/react";
import SectionHeader from "../../../CustomUi/SectionHeader";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import TextSetting from "./TextSetting";
import { ApiDataResponse, ApiResponse } from "../../../../services/Dtos";
import ColorSetting from "./ColorSetting";
import { ImageUpload } from "../../../CustomUi/ImageUpload";
import CustomAlert from "../../../CustomUi/CustomAlert";
import Loader from "../../../CustomUi/Loader";
import useToaster from "../../../../hooks/useToaster";
import { getInitials } from "../../../../services/Utils";
import useAxios from "../../../../hooks/useAxios";
import useThemeColors from "../../../../hooks/useThemeColors";
import useIcons from "../../../../hooks/useIcons";
import Cad from "../../../CustomUi/Cad";
import CustomButton from "../../../CustomUi/CustomButton";

const SchoolSettings: React.FC = () => {
  const { school, setSchool, isLoading, error } =
    useSchoolsContext();
  const { showSuccessToast, showErrorToast } = useToaster();
  const [cover, setCover] = useState<string | null>(null);
  const [schoolLogo, setSchoolLogo] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const axiosClient = useAxios();
  const colors = useThemeColors();
  const icons = useIcons();

  const handleGeneralSettingsSubmit = (property: string, value: string) => {
    setSchool((prevSchool) =>
      prevSchool ? { ...prevSchool, [property]: value } : prevSchool
    );
  };

  const handleGeneralSettingsSave = async () => {
    try {
      const response = await axiosClient.put<ApiResponse>(`schools`, school);
      if (response.data.succeeded) {
        showSuccessToast(response.data.message || "");
      } else {
        showErrorToast(response.data.message || "");
      }
    } catch (err) {}
  };

  const handleColorUpload = async (
    schoolColor: string
  ): Promise<ApiResponse | null> => {
    if (!schoolColor) return null;

    try {
      const response = await axiosClient.put<ApiResponse>("schools", {
        accentColor: schoolColor,
      });
      if (response.data.succeeded) {
        setSchool((prevSchool) =>
          prevSchool
            ? { ...prevSchool, ["accentColor"]: schoolColor }
            : prevSchool
        );
      }
      return response.data;
    } catch (error) {
    }
    return null;
  };

  const handleColorReset = async (): Promise<ApiResponse | null> => {
    try {
      const response = await axiosClient.put<ApiResponse>("schools", {
        accentColor: "brand",
      });
      if (response.data.succeeded) {
        setSchool((prevSchool) =>
          prevSchool ? { ...prevSchool, ["accentColor"]: "brand" } : prevSchool
        );
      }
      return response.data;
    } catch (error) {
    }
    return null;
  };

  const handleImageUpload = async (
    image: string | null,
    type: string,
    endpoint: string
  ): Promise<ApiResponse | null> => {
    if (!image) return null;

    const blob = await fetch(image).then((res) => res.blob());
    const formData = new FormData();
    formData.append("file", blob);

    try {
      const response = await axiosClient.post<ApiDataResponse<string>>(
        endpoint,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.succeeded) {
        setSchool((prevSchool) =>
          prevSchool
            ? { ...prevSchool, [type]: response.data.data }
            : prevSchool
        );
      }
      return response.data;
    } catch (error) {
    }
    return null;
  };

  const handleDelete = async (type: string, deleteEndpoint: string) => {
    try {
      const response = await axiosClient.delete<ApiResponse>(deleteEndpoint);
      if (response.data.succeeded) {
        setSchool((prevSchool) =>
          prevSchool ? { ...prevSchool, [type]: "" } : prevSchool
        );
      }
      return response.data;
    } catch (error) {
    }
    return null;
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <CustomAlert message={error} handleClick={() => console.log(error)} />
    );
  }

  return (
    <VStack gap={8}>
      {/* General Settings Section */}
      <Cad>
        <VStack gap={6} align="stretch">
          <SectionHeader
            title="General Information"
            subtitle="Configure basic details about your school"
            icon={icons.main.school}
          />

          <Separator />

          {/* General Settings Grid */}
          <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6}>
            <GridItem>
              <TextSetting
                handleSubmit={handleGeneralSettingsSubmit}
                label="School Name"
                property="name"
                defaultValue={school?.name}
              />
            </GridItem>
            <GridItem>
              <TextSetting
                handleSubmit={handleGeneralSettingsSubmit}
                label="School Phone Number"
                property="phoneNumber"
                defaultValue={school?.phoneNumber}
              />
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 2 }}>
              <TextSetting
                handleSubmit={handleGeneralSettingsSubmit}
                label="School Address"
                property="address"
                defaultValue={school?.address}
              />
            </GridItem>
            <GridItem>
              <TextSetting
                handleSubmit={handleGeneralSettingsSubmit}
                label="School Website"
                property="website"
                defaultValue={school?.website}
              />
            </GridItem>
            <GridItem>
              <TextSetting
                handleSubmit={handleGeneralSettingsSubmit}
                label="School Email"
                property="email"
                defaultValue={school?.email}
              />
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 2 }}>
              <TextSetting
                handleSubmit={handleGeneralSettingsSubmit}
                label="School Slogan or Footnote"
                property="footnote"
                defaultValue={school?.footnote}
              />
            </GridItem>
          </Grid>

          <Separator />

          {/* Advanced Settings */}
          <VStack gap={1} align="start">
            <HStack gap={2} align="center">
              <Icon as={icons.utility.info} color={colors.schoolAccent.icon} boxSize={4} />
              <Heading size="sm" color={colors.text.primary}>
                Advanced Configuration
              </Heading>
            </HStack>
            <Text fontSize="sm" color={colors.text.secondary}>
              Configure ID prefixes and formatting options
            </Text>
          </VStack>

          <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6}>
            <GridItem>
              <TextSetting
                handleSubmit={handleGeneralSettingsSubmit}
                label="Student ID Prefix"
                property="studentIdPrefix"
                defaultValue={school?.studentIdPrefix}
                placeHolder={`${getInitials(school?.name || "")}/{YEAR}`}
                body="Use {YEAR} - for current year, {MONTH} - for current month, {DAY} - for the current day, {SESSION} - for the current session and {TERM} - for the current term."
              />
            </GridItem>
            <GridItem>
              <TextSetting
                handleSubmit={handleGeneralSettingsSubmit}
                label="Result ID Prefix"
                property="resultIdPrefix"
                defaultValue={school?.resultIdPrefix}
                placeHolder=""
                body="Use {YEAR} - for current year, {MONTH} - for current month, {DAY} - for the current day, {SESSION} - for the current session and {TERM} - for the current term."
              />
            </GridItem>
          </Grid>

          {/* Save Button */}
          <Flex flexDir={["column"]} align={["stretch", "flex-start"]} gap={2}>
            <CustomButton
              colorPalette={colors.schoolAccent.primary}
              label="Save General Settings"
              onClick={handleGeneralSettingsSave}
            />
          </Flex>
        </VStack>
      </Cad>

      {/* Branding Section */}
      <Cad>
        <VStack gap={6} align="stretch">
          <SectionHeader
            title="School Branding"
            subtitle="Customize your school's visual identity and colors"
            icon={icons.utility.color}
          />

          <Separator />

          {/* Color Setting */}
          <Box>
            <ColorSetting
              color={school?.accentColor || "brand"}
              handleSave={handleColorUpload}
              handleReset={handleColorReset}
              successMessage=""
              errorMessage=""
              heading="School Accent Color"
              body="Please select an accent color that complements your school brand."
              buttonText=""
            />
          </Box>

          <Separator />

          {/* Image Uploads */}
          <VStack gap={8} align="stretch">
            <ImageUpload
              handleDelete={() =>
                handleDelete("avatar", "schools/deleteavatar")
              }
              image={school?.avatar ?? null}
              handleUpload={() =>
                handleImageUpload(schoolLogo, "avatar", "schools/uploadavatar")
              }
              successMessage=""
              errorMessage=""
              heading="School Logo"
              body="Please upload an image to serve as your school's logo. This logo will be prominently displayed through the app for your school branding and result printouts."
              buttonText=""
              media={schoolLogo}
              setMedia={setSchoolLogo}
            />

            <ImageUpload
              handleDelete={() =>
                handleDelete("coverPhoto", "schools/deletecoverphoto")
              }
              image={school?.coverPhoto ?? null}
              handleUpload={() =>
                handleImageUpload(
                  cover,
                  "coverPhoto",
                  "schools/uploadcoverphoto"
                )
              }
              successMessage=""
              errorMessage=""
              heading="School Cover Photo"
              body="Upload a cover photo that will be prominently displayed throughout the app for your school branding."
              buttonText=""
              media={cover}
              setMedia={setCover}
            />

            <ImageUpload
              handleDelete={() =>
                handleDelete("adminSignature", "schools/deletesignature")
              }
              image={school?.adminSignature ?? null}
              handleUpload={() =>
                handleImageUpload(
                  signature,
                  "adminSignature",
                  "schools/uploadsignature"
                )
              }
              successMessage=""
              errorMessage=""
              heading="Admin Signature"
              body="Upload a signature that will appear on PDF result printouts. Ensure the signature is clear and high-quality for the best representation."
              buttonText=""
              media={signature}
              setMedia={setSignature}
            />
          </VStack>
        </VStack>
      </Cad>
    </VStack>
  );
};

export default SchoolSettings;
